import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Building2,
  Calendar,
  ChevronDown,
  Clock3,
  FileCheck,
  FileText,
  Shield,
  Wallet,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getFinanceDashboard } from "../../api/finance/dashboard";

const STATUS_COLORS = {
  waiting: "#f59e0b",
  approved: "#10b981",
  rejected: "#ef4444",
  completed: "#2563eb",
};

const SOURCE_COLORS = ["#ef4444", "#f97316", "#2563eb", "#14b8a6", "#8b5cf6", "#64748b"];

function formatCurrencyCompact(value) {
  const amount = Number(value || 0);
  const abs = Math.abs(amount);
  const opts = { maximumFractionDigits: 0, minimumFractionDigits: 0 };

  if (abs >= 1_000_000_000) {
    return `${(amount / 1_000_000_000).toLocaleString("vi-VN", opts)} tỷ`;
  }
  if (abs >= 1_000_000) {
    return `${(amount / 1_000_000).toLocaleString("vi-VN", opts)} triệu`;
  }
  return `${amount.toLocaleString("vi-VN", opts)} ₫`;
}

function formatCurrencyFull(value) {
  return `${Number(value || 0).toLocaleString("vi-VN", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })} ₫`;
}

function formatDateShort(value) {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("vi-VN");
}

function truncateText(value, maxLength = 80) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}…`;
}

function toMonthInputValue(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function EmptyChartState({ message }) {
  return (
    <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-400">
      {message}
    </div>
  );
}

function getActivityStatusMeta(type, status) {
  if (type === "receipt") {
    if (status === "posted") {
      return { label: "Đã ghi nhận", className: "bg-emerald-50 text-emerald-700" };
    }
    if (status === "draft") {
      return { label: "Nháp", className: "bg-amber-50 text-amber-700" };
    }
    if (status === "voided") {
      return { label: "Hủy", className: "bg-red-50 text-red-700" };
    }
  }

  if (status === "waiting") {
    return { label: "Chờ duyệt", className: "bg-amber-50 text-amber-700" };
  }
  if (status === "approved") {
    return { label: "Đã duyệt", className: "bg-emerald-50 text-emerald-700" };
  }
  if (status === "rejected") {
    return { label: "Từ chối", className: "bg-red-50 text-red-700" };
  }
  if (status === "completed") {
    return { label: "Hoàn thành", className: "bg-blue-50 text-blue-700" };
  }

  return { label: status || "—", className: "bg-gray-50 text-gray-600" };
}

export default function FinanceDashboard() {
  const now = new Date();
  const navigate = useNavigate();

  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const monthPickerValue = toMonthInputValue(selectedYear, selectedMonth);

  const handleMonthChange = (event) => {
    const value = event.target.value;
    if (!value) return;
    const [year, month] = value.split("-").map(Number);
    if (!Number.isFinite(year) || !Number.isFinite(month)) return;
    setSelectedYear(year);
    setSelectedMonth(month);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await getFinanceDashboard({
          year: selectedYear,
          month: selectedMonth,
          branch_id: selectedBranch,
        });
        if (!isMounted) return;
        setDashboard(data);
      } catch (err) {
        console.error("Failed to load finance dashboard:", err);
        if (!isMounted) return;
        setDashboard(null);
        setError("Không thể tải dashboard tài chính.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDashboard();

    return () => {
      isMounted = false;
    };
  }, [selectedYear, selectedMonth, selectedBranch]);

  useEffect(() => {
    const nextBranch = dashboard?.meta?.selectedBranchId;
    if (nextBranch && nextBranch !== selectedBranch) {
      setSelectedBranch(nextBranch);
    }
  }, [dashboard?.meta?.selectedBranchId, selectedBranch]);

  const scopeLabel = dashboard?.meta?.scopeLabel || "";
  const isSuperAdmin = dashboard?.meta?.isSuperAdmin === true;
  const branchOptions = dashboard?.meta?.branches || [];
  const selectedBranchLabel = dashboard?.meta?.selectedBranchLabel || "Theo phạm vi phân quyền";
  const showBranchFilter = !loading && (dashboard?.meta?.canPickBranch || false);

  const cashflowData = dashboard?.charts?.cashflow || [];
  const receiptSources = useMemo(
    () =>
      (dashboard?.charts?.receiptSources || []).map((item, index) => ({
        ...item,
        fill: SOURCE_COLORS[index % SOURCE_COLORS.length],
      })),
    [dashboard?.charts?.receiptSources]
  );
  const requestStatuses = dashboard?.charts?.requestStatuses || [];
  const recentActivities = dashboard?.recentActivities || [];

  const statsCards = useMemo(() => {
    const stats = dashboard?.stats;
    if (!stats) return [];

    return [
      {
        key: "receipts-month",
        label: "Thực thu tháng",
        sub: `${stats.receipts.monthCount} phiếu • ${formatCurrencyCompact(
          stats.receipts.yearAmount
        )} trong năm`,
        value: formatCurrencyCompact(stats.receipts.monthAmount),
        icon: ArrowUpCircle,
        color: "bg-emerald-50",
        text: "text-emerald-600",
        detailHref: "/finance/income_expenditure",
      },
      {
        key: "outflow-month",
        label: "Chi đã duyệt tháng",
        sub: `${formatCurrencyCompact(
          stats.outflow.approvedPaymentsMonthAmount
        )} thanh toán • ${formatCurrencyCompact(
          stats.outflow.approvedAdvancesMonthAmount
        )} tạm ứng`,
        value: formatCurrencyCompact(stats.outflow.monthAmount),
        icon: ArrowDownCircle,
        color: "bg-rose-50",
        text: "text-rose-600",
        detailHref: "/finance/payment_requests",
      },
      {
        key: "net-month",
        label: "Dòng tiền thuần",
        sub: "Thu trong tháng trừ chi đã duyệt",
        value: formatCurrencyCompact(stats.net.monthAmount),
        icon: Wallet,
        color: "bg-sky-50",
        text: "text-sky-600",
        detailHref: "/finance/income_expenditure",
      },
      {
        key: "waiting",
        label: "Hồ sơ chờ duyệt",
        sub: `${stats.approvals.waitingPayments} thanh toán • ${stats.approvals.waitingAdvances} tạm ứng`,
        value: `${stats.approvals.waitingCount}`,
        icon: Clock3,
        color: "bg-amber-50",
        text: "text-amber-600",
        detailHref: "/finance/payment_requests",
      },
      {
        key: "approved-month",
        label: "Hồ sơ đã xử lý tháng",
        sub: "Đã duyệt / hoàn tất trong tháng",
        value: `${stats.approvals.approvedMonthCount}`,
        icon: FileCheck,
        color: "bg-violet-50",
        text: "text-violet-600",
        detailHref: "/finance/advance",
      },
      {
        key: "advance-completed",
        label: "Tạm ứng quyết toán",
        sub: `Hoàn thành trong tháng ${selectedMonth}/${selectedYear}`,
        value: `${stats.advances.completedMonthCount}`,
        icon: FileText,
        color: "bg-cyan-50",
        text: "text-cyan-700",
        detailHref: "/finance/advance",
      },
    ];
  }, [dashboard, selectedMonth, selectedYear]);

  return (
    <div className="min-h-screen space-y-8 bg-[#F8FAFC] p-6 pb-20">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-tight text-gray-800">
            Báo cáo tài chính
          </h1>
          <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400 italic">
            Theo dõi dòng tiền, phê duyệt và hiệu suất thu chi
          </p>
          {!loading ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {scopeLabel ? (
                <div className="inline-flex items-center gap-2 rounded-2xl border border-red-100 bg-red-50/80 px-3 py-2 text-[11px] font-black uppercase tracking-wide text-red-800">
                  <Shield className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                  {scopeLabel}
                </div>
              ) : null}
              {isSuperAdmin ? (
                <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-black uppercase tracking-wide text-slate-700">
                  <Building2 className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                  {selectedBranchLabel}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 lg:max-w-[min(100%,31rem)] lg:flex-nowrap">
          {showBranchFilter ? (
            <div className="relative min-w-[15rem] flex-1">
              <div className="absolute inset-y-0 left-4 z-10 flex items-center text-red-600 pointer-events-none">
                <Building2 size={16} strokeWidth={3} />
              </div>
              <select
                value={selectedBranch}
                onChange={(event) => setSelectedBranch(event.target.value)}
                className="h-[50px] w-full appearance-none rounded-2xl border border-gray-100 bg-white py-3 pl-11 pr-12 text-sm font-black text-gray-700 shadow-sm outline-none transition-all focus:border-red-200 focus:ring-2 focus:ring-red-100"
                aria-label="Chọn chi nhánh dashboard tài chính"
              >
                <option value="all">Tất cả chi nhánh</option>
                {branchOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-4 flex items-center text-gray-400 pointer-events-none">
                <ChevronDown size={16} strokeWidth={3} />
              </div>
            </div>
          ) : null}

          <div className="relative min-w-0 shrink-0">
            <div className="absolute inset-y-0 left-4 z-10 flex items-center text-red-600 pointer-events-none">
              <Calendar size={16} strokeWidth={3} />
            </div>
            <input
              type="month"
              value={monthPickerValue}
              onChange={handleMonthChange}
              aria-label="Chọn tháng xem dashboard tài chính"
              className="h-[50px] min-w-[11rem] rounded-2xl border border-gray-100 bg-white py-3 pl-11 pr-3 text-sm font-black uppercase tracking-wide text-gray-700 shadow-sm outline-none transition-all focus:border-red-200 focus:ring-2 focus:ring-red-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
            />
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-[2rem] border border-red-100 bg-white px-6 py-4 text-sm font-semibold text-red-600 shadow-sm">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {loading ? (
          <div className="col-span-full py-10 text-center text-gray-500">Đang tải dashboard...</div>
        ) : (
          statsCards.map((item, index) => {
            const openDetail = () => {
              if (item.detailHref) navigate(item.detailHref);
            };

            return (
              <motion.div
                key={item.key}
                role={item.detailHref ? "link" : undefined}
                tabIndex={item.detailHref ? 0 : undefined}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={item.detailHref ? openDetail : undefined}
                onKeyDown={
                  item.detailHref
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openDetail();
                        }
                      }
                    : undefined
                }
                className={`group relative overflow-hidden rounded-[2rem] border border-gray-50 bg-white p-6 shadow-sm transition-all ${
                  item.detailHref
                    ? "cursor-pointer hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-red-200 focus-visible:ring-offset-2"
                    : ""
                }`}
              >
                <span className="pointer-events-none absolute right-4 top-3 z-10 text-[8px] font-black uppercase tracking-[0.2em] text-red-500 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                  Chi tiết
                </span>
                <div className="relative z-10 flex items-start justify-between">
                  <div className={`rounded-2xl p-3 ${item.color} ${item.text} transition-transform group-hover:scale-110`}>
                    <item.icon size={24} />
                  </div>
                </div>
                <div className="relative z-10 mt-4">
                  <div className="text-3xl font-black tracking-tight text-gray-800">{item.value}</div>
                  <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">
                    {item.label}
                  </div>
                  <div className="mt-0.5 text-[11px] font-semibold text-gray-500">{item.sub}</div>
                </div>
                <div className={`absolute -bottom-5 -right-5 h-24 w-24 rounded-full opacity-30 ${item.color}`} />
              </motion.div>
            );
          })
        )}
      </div>

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-[1.8fr,1fr]">
        <motion.div
          className="rounded-[2.5rem] border border-gray-100 bg-white p-8 shadow-sm"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h2 className="mb-2 flex items-center gap-2 text-lg font-black uppercase tracking-tight text-gray-800">
            <Wallet className="text-red-700" size={20} />
            Dòng tiền theo tháng
          </h2>
          <p className="mb-6 text-[10px] font-bold uppercase tracking-widest text-gray-400">
            Thu thực tế so với chi đã duyệt trong năm {selectedYear}
          </p>
          <div className="h-[340px] w-full">
            {loading ? (
              <EmptyChartState message="Đang tải biểu đồ..." />
            ) : cashflowData.every(
                (item) =>
                  Number(item.receiptsAmount) === 0 &&
                  Number(item.outflowAmount) === 0 &&
                  Number(item.netAmount) === 0
              ) ? (
              <EmptyChartState message="Chưa có dữ liệu thu chi trong năm đã chọn." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={cashflowData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                    width={42}
                    tickFormatter={(value) => formatCurrencyCompact(value)}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === "receiptsAmount") return [formatCurrencyFull(value), "Thực thu"];
                      if (name === "outflowAmount") return [formatCurrencyFull(value), "Chi đã duyệt"];
                      return [formatCurrencyFull(value), "Dòng tiền thuần"];
                    }}
                    labelFormatter={(label) => `Tháng ${String(label).replace("T", "")}`}
                  />
                  <Legend
                    formatter={(value) => {
                      if (value === "receiptsAmount") return "Thực thu";
                      if (value === "outflowAmount") return "Chi đã duyệt";
                      return "Dòng tiền thuần";
                    }}
                  />
                  <Bar
                    dataKey="receiptsAmount"
                    fill="#10b981"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={22}
                    name="receiptsAmount"
                  />
                  <Bar
                    dataKey="outflowAmount"
                    fill="#ef4444"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={22}
                    name="outflowAmount"
                  />
                  <Line
                    type="monotone"
                    dataKey="netAmount"
                    name="netAmount"
                    stroke="#2563eb"
                    strokeWidth={3}
                    dot={{ r: 4, fill: "#2563eb" }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </motion.div>

        <motion.div
          className="rounded-[2.5rem] border border-gray-100 bg-white p-8 shadow-sm"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <h2 className="mb-2 flex items-center gap-2 text-lg font-black uppercase tracking-tight text-gray-800">
            <ArrowUpCircle className="text-emerald-600" size={20} />
            Cơ cấu nguồn thu
          </h2>
          <p className="mb-6 text-[10px] font-bold uppercase tracking-widest text-gray-400">
            Theo phiếu thu đã ghi nhận trong tháng {selectedMonth}/{selectedYear}
          </p>
          <div className="h-[340px] w-full">
            {loading ? (
              <EmptyChartState message="Đang tải biểu đồ..." />
            ) : receiptSources.length === 0 ? (
              <EmptyChartState message="Chưa có dữ liệu nguồn thu trong tháng." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={receiptSources} layout="vertical" margin={{ top: 8, right: 12, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => formatCurrencyCompact(value)}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#64748b", fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                    width={96}
                  />
                  <Tooltip
                    formatter={(value) => [formatCurrencyFull(value), "Giá trị thu"]}
                    labelFormatter={(label) => label}
                  />
                  <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                    {receiptSources.map((entry) => (
                      <Cell key={entry.key} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          {receiptSources.length > 0 ? (
            <div className="mt-5 space-y-2">
              {receiptSources.map((item) => (
                <div key={item.key} className="flex items-center justify-between gap-3 rounded-2xl bg-gray-50 px-4 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: item.fill }}
                    />
                    <span className="truncate text-sm font-semibold text-gray-700">{item.label}</span>
                  </div>
                  <span className="shrink-0 text-sm font-black text-gray-800">
                    {formatCurrencyCompact(item.value)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </motion.div>
      </div>

      <motion.div
        className="rounded-[2.5rem] border border-gray-100 bg-white p-8 shadow-sm"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="mb-2 flex items-center gap-2 text-lg font-black uppercase tracking-tight text-gray-800">
          <FileCheck className="text-violet-600" size={20} />
          Trạng thái hồ sơ tài chính
        </h2>
        <p className="mb-6 text-[10px] font-bold uppercase tracking-widest text-gray-400">
          Tổng hợp đề nghị thanh toán và tạm ứng trong năm {selectedYear}
        </p>
        <div className="h-[320px] w-full">
          {loading ? (
            <EmptyChartState message="Đang tải biểu đồ..." />
          ) : requestStatuses.every(
              (item) =>
                Number(item.waiting) === 0 &&
                Number(item.approved) === 0 &&
                Number(item.rejected) === 0 &&
                Number(item.completed) === 0
            ) ? (
            <EmptyChartState message="Chưa có dữ liệu hồ sơ trong năm đã chọn." />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={requestStatuses} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="module"
                  tick={{ fontSize: 11, fill: "#64748b", fontWeight: 700 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                  axisLine={false}
                  tickLine={false}
                  width={36}
                />
                <Tooltip />
                <Legend />
                <Bar dataKey="waiting" stackId="a" fill={STATUS_COLORS.waiting} name="Chờ duyệt" radius={[6, 6, 0, 0]} />
                <Bar dataKey="approved" stackId="a" fill={STATUS_COLORS.approved} name="Đã duyệt" />
                <Bar dataKey="rejected" stackId="a" fill={STATUS_COLORS.rejected} name="Từ chối" />
                <Bar dataKey="completed" stackId="a" fill={STATUS_COLORS.completed} name="Hoàn thành" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </motion.div>

      <motion.div
        className="rounded-[2.5rem] border border-gray-100 bg-white p-8 shadow-sm"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55 }}
      >
        <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-black uppercase tracking-tight text-gray-800">
              <FileText className="text-red-700" size={20} />
              Hoạt động tài chính gần đây
            </h2>
            <p className="mt-2 text-[10px] font-bold uppercase tracking-widest text-gray-400">
              Giao dịch và hồ sơ phát sinh trong tháng {selectedMonth}/{selectedYear}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/finance/income_expenditure")}
              className="inline-flex items-center justify-center rounded-full border border-red-100 bg-red-50 px-4 py-2 text-xs font-black uppercase tracking-wider text-red-700 transition hover:border-red-200 hover:bg-red-100"
            >
              Thu — Chi
            </button>
            <button
              type="button"
              onClick={() => navigate("/finance/payment_requests")}
              className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-wider text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
            >
              Thanh toán
            </button>
            <button
              type="button"
              onClick={() => navigate("/finance/advance")}
              className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-wider text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
            >
              Tạm ứng
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] table-fixed border-collapse text-left">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="w-[16%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Loại
                </th>
                <th className="w-[14%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Mã hồ sơ
                </th>
                <th className="w-[16%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Đối tượng
                </th>
                <th className="w-[14%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Chi nhánh
                </th>
                <th className="w-[14%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Số tiền
                </th>
                <th className="w-[10%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Ngày
                </th>
                <th className="w-[12%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Trạng thái
                </th>
                <th className="w-[14%] px-4 pb-4 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Nội dung
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-sm font-semibold text-gray-400">
                    Đang tải dữ liệu hoạt động...
                  </td>
                </tr>
              ) : recentActivities.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-sm font-semibold text-gray-400">
                    Không có hoạt động nào trong bộ lọc hiện tại.
                  </td>
                </tr>
              ) : (
                recentActivities.map((item) => {
                  const statusMeta = getActivityStatusMeta(item.type, item.status);
                  const openDetail = () => {
                    if (item.type === "receipt") navigate("/finance/income_expenditure");
                    else if (item.type === "payment") navigate("/finance/payment_requests");
                    else navigate("/finance/advance");
                  };

                  return (
                    <tr
                      key={`${item.type}-${item.id}`}
                      className="group cursor-pointer transition-colors hover:bg-gray-50/60"
                      onClick={openDetail}
                    >
                      <td className="px-4 py-5 align-top">
                        <div className="font-black text-sm text-gray-800">{item.type_label}</div>
                      </td>
                      <td className="px-4 py-5 align-top text-sm font-semibold text-gray-700">
                        {item.code || "—"}
                      </td>
                      <td className="px-4 py-5 align-top text-sm font-semibold text-gray-700">
                        {item.actor_name || "—"}
                      </td>
                      <td className="px-4 py-5 align-top text-sm text-gray-600">
                        {item.branch_name || "—"}
                      </td>
                      <td className="px-4 py-5 align-top text-sm font-black text-gray-800">
                        {formatCurrencyFull(item.amount)}
                      </td>
                      <td className="px-4 py-5 align-top text-sm text-gray-600">
                        {formatDateShort(item.occurred_at)}
                      </td>
                      <td className="px-4 py-5 align-top">
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${statusMeta.className}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="px-4 py-5 align-top text-sm text-gray-600">
                        {truncateText(item.note, 56)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
